import { Theme } from '@mui/material';
import '../themeAugmentations';

export const FONT_FAMILY = `'Inclusive Sans', 'Lato', 'sans-serif'`;

export const DEFAULT_BORDER_RADIUS = 12;

export const BORDER_RADIUS: Theme['app']['borderRadius'] = {
  slightlyRounded: '4px',
  lessRounded: '8px',
  moreRounded: `${DEFAULT_BORDER_RADIUS}px`,
  rounded: '100px',
};

const FONT_SIZE: Theme['app']['fontSize'] = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 24,
  xl: 36,
  xxl: 48,
  xxxl: 64,
};

const FONT_WEIGHT: Theme['app']['fontWeight'] = {
  regular: 400,
  semibold: 500,
  bold: 700,
};

const LETTER_SPACING: Theme['app']['letterSpacing'] = {
  tight: -0.25,
  slightlyTight: -0.125,
  neutral: 0,
  slightlyLoose: 0.1,
  loose: 0.25,
};

const LINE_HEIGHT: Theme['app']['lineHeight'] = {
  tight: 16,
  compact: 20,
  standard: 24,
  extended: 32,
  spacious: 48,
  expansive: 64,
};

export const UNIVERSAL_THEME_ELEMENTS = {
  app: {
    borderRadius: BORDER_RADIUS,
    fontSize: FONT_SIZE,
    fontWeight: FONT_WEIGHT,
    letterSpacing: LETTER_SPACING,
    lineHeight: LINE_HEIGHT,
  },
  shape: {
    borderRadius: DEFAULT_BORDER_RADIUS,
  },
  typography: {
    fontFamily: FONT_FAMILY,
    displayLarge: {
      fontSize: '57px',
      lineHeight: '64px',
      fontWeight: 400,
      letterSpacing: LETTER_SPACING.tight,
    },
    displayMedium: {
      fontSize: '45px',
      lineHeight: '52px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    displaySmall: {
      fontSize: '36px',
      lineHeight: '44px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    headlineLarge: {
      fontSize: '32px',
      lineHeight: '40px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    headlineMedium: {
      fontSize: '28px',
      lineHeight: '36px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    headlineSmall: {
      fontSize: '24px',
      lineHeight: '32px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    titleLarge: {
      fontSize: '22px',
      lineHeight: '28px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    titleMedium: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    titleSmall: {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: FONT_WEIGHT.semibold,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    subheadLarge: {
      fontSize: '20px',
      lineHeight: '28px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    subheadMedium: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    subheadSmall: {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    bodyLarge: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    bodyMedium: {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    bodySmall: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    labelLarge: {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: FONT_WEIGHT.semibold,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    labelMedium: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    labelSmall: {
      fontSize: '10px',
      lineHeight: '16px',
      fontWeight: FONT_WEIGHT.regular,
      letterSpacing: LETTER_SPACING.slightlyTight,
    },
    subtitle1: {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: FONT_WEIGHT.semibold,
    },
    subtitle2: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: FONT_WEIGHT.regular,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        html body {
          font-family: ${FONT_FAMILY};
        }

      `,
    },
    MuiAvatar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primaryAccent.main,
          color: 'white',
          width: '32px',
          height: '32px',
          padding: '8px',
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          '&.reusable-toast-alert': {
            width: '100%',
          },
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '&.reusable-toast-snackbar': {
            zIndex: 9999,
          },
        },
      },
    },
    MuiButton: {
      variants: [],
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.labelLarge,

          fontWeight: FONT_WEIGHT.semibold,
          padding: '10px 16px',
          width: 'fit-content',
          height: 'fit-content !important',
          textTransform: 'none',
          borderRadius: BORDER_RADIUS.rounded,
          boxShadow: 'none',

          display: 'inline-flex',
          alignItems: 'center',

          '&:not(.preserve-icon-size) svg': {
            height: 20,
            width: 20,
          },

          '&.Mui-disabled': {
            opacity: '1 !important',
            color: theme.palette.greyShades?.[300],
            backgroundColor: theme.palette.greyShades?.[250],
          },

          '&:hover': {
            boxShadow: 'none',
          },

          '&.side-menu-notification-trigger': {
            margin: 0,
            padding: 0,
            minWidth: 0,
            width: '100%',
            borderRadius: theme.app.borderRadius.lessRounded,
            overflow: 'hidden',
            flexShrink: 0,
            boxSizing: 'border-box',
          },
        }),

        outlined: {
          padding: '9px 16px',
        },

        startIcon: {
          margin: '-2px 8px -2px -8px',
          padding: 0,

          display: 'inline-flex',
          alignItems: 'center',
        },

        endIcon: {
          margin: '-2px -8px -2px 8px',

          display: 'inline-flex',
          alignItems: 'center',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        root: {
          '&.notification-center-anchor': {
            cursor: 'pointer',
            '& .MuiBadge-badge': {
              margin: '14px 0 0 8px',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.notification-center-bell': {
            color: 'white',
            width: 'fit-content',
            height: 'fit-content',
            '&:hover': {
              backgroundColor: '#5dd4fc29',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.labelLarge,

          // Default chip styles
          borderRadius: theme.app.borderRadius.rounded,
          height: '32px',
          padding: '8px 16px',
          fontWeight: theme.app.fontWeight.semibold,
          letterSpacing: theme.app.letterSpacing.neutral,
          width: 'fit-content',
          minWidth: '50px',
          flexShrink: 0,

          // Tag chip styles (when className includes 'tag')
          '&.tag': {
            ...theme.typography.labelMedium,

            borderRadius: theme.app.borderRadius.slightlyRounded,
            height: '24px',
            padding: '2px 6px',
            fontWeight: theme.app.fontWeight.regular,
            letterSpacing: theme.app.letterSpacing.tight,
          },
        }),

        iconSmall: {
          width: 16,
          height: 16,
          display: 'flex',
          marginLeft: 0,
          marginRight: 1,
          '& svg': {
            width: '100%',
            height: '100%',
          },
        },

        iconMedium: {
          width: 20,
          height: 20,
          display: 'flex',
          marginLeft: 0,
          marginRight: 1,
          '& svg': {
            width: '100%',
            height: '100%',
          },
        },

        deleteIconSmall: {
          width: 16,
          height: 16,
          display: 'flex',
          marginLeft: 1,
          marginRight: 0,
          '& svg': {
            width: '100%',
            height: '100%',
          },
        },

        deleteIconMedium: {
          width: 20,
          height: 20,
          display: 'flex',
          marginLeft: 1,
          marginRight: 0,
          '& svg': {
            width: '100%',
            height: '100%',
          },
        },

        label: {
          lineHeight: 2,
          padding: 0,
          margin: 0,
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),

        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,

          '&.Mui-focused': {
            color: theme.palette.text.secondary,
          },

          '&.Mui-error': {
            color: theme.palette.error.main,
          },
        }),
      },
    },
    MuiSpeedDial: {
      styleOverrides: {
        actions: {
          marginTop: 8,
        },
        fab: ({ theme }) => ({
          backgroundColor: theme.palette.primaryAccent.main,
          elevation: 2,

          '&:hover': {
            backgroundColor: theme.palette?.peachShades?.[400],
          },
        }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 'max-content',
          justifySelf: 'center',

          '&.toggle-switch': {
            borderRadius: theme.app.borderRadius.rounded,
            backgroundColor: theme.palette.action.hover,
            padding: 4,
            display: 'flex',
            overflow: 'hidden',

            '&.MuiToggleButtonGroup-fullWidth': {
              width: '100%',
              boxSizing: 'border-box',
            },

            '& .MuiToggleButtonGroup-grouped:first-of-type': {
              borderRadius: theme.app.borderRadius.rounded,
              borderRight: 'none !important',
            },
            '& .MuiToggleButtonGroup-grouped:last-of-type': {
              borderRadius: theme.app.borderRadius.rounded,
            },
          },
        }),

        grouped: {
          '.toggle-switch &': {
            border: 'none !important',
            margin: 0,
            borderRadius: '0px !important',

            '&:not(:first-of-type)': {
              borderLeft: 'none !important',
              '&.MuiToggleButton-root': {
                borderLeft: 'none !important',
              },
            },
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.toggle-switch &': {
            position: 'relative',
            backgroundColor: 'transparent',
            border: 'none !important',

            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: theme.app.fontWeight.regular,
            color: theme.palette.text.primary,
            padding: '10px 16px',
            zIndex: 1,

            '&:hover': {
              backgroundColor: 'transparent',
            },
          },

          '.toggle-switch &.Mui-selected': {
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&[color="primary"]': {
            color: 'primary.main !important',
          },
          '&[color="secondary"]': {
            color: 'secondary.main !important',
          },
          '&[color="tertiary"]': {
            color: 'tertiary.main !important',
          },
          '&[color="error"]': {
            color: 'error.main !important',
          },
          '&[color="textPrimary"]': {
            color: 'text.primary !important',
          },
          '&[color="textSecondary"]': {
            color: 'text.secondary !important',
          },
          '&.truncate': {
            whiteSpace: 'nowrap !important',
            overflow: 'hidden !important',
            textOverflow: 'ellipsis !important',
          },
          '&.break-word': {
            wordBreak: 'break-word !important',
          },
        },
      },
      defaultProps: {
        variantMapping: {
          displayLarge: 'h1',
          displayMedium: 'h2',
          displaySmall: 'h3',
          headlineLarge: 'h4',
          headlineMedium: 'h5',
          headlineSmall: 'h6',
          titleLarge: 'h5',
          titleMedium: 'h6',
          titleSmall: 'p',
          subheadLarge: 'p',
          subheadMedium: 'p',
          subheadSmall: 'p',
          bodyLarge: 'p',
          bodyMedium: 'p',
          bodySmall: 'p',
          labelLarge: 'span',
          labelMedium: 'span',
          labelSmall: 'span',
          subtitle1: 'p',
          subtitle2: 'p',
        },
      },
    },
  },
};
